import { useCallback, useEffect, useState } from 'react';

/**
 * Form accessibility helpers (QA 2026-09-25 #18, #30, #31): one rule for
 * where focus goes when a form is refused, and one email check, shared by
 * the booking contact step and the auth pages.
 */

/** The same shape the booking step and the server accept. */
export const isValidEmail = (value: string): boolean => (
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
);

const INVALID = '[aria-invalid="true"]';

/**
 * Focus the first field marked aria-invalid inside `root` (document order),
 * scrolling it to the middle of the screen so the sticky header and the
 * step's bottom bar cannot cover it. Returns whether one was found.
 */
export function focusFirstInvalid(root: ParentNode | null | undefined): boolean {
  if (!root) return false;
  const field = root.querySelector<HTMLElement>(INVALID);
  if (!field) return false;
  field.focus({ preventScroll: true });
  if (typeof field.scrollIntoView === 'function') {
    field.scrollIntoView({ block: 'center' });
  }
  return true;
}

/**
 * Runs focusFirstInvalid after the render that shows a refused submit's
 * errors. Call the returned function from the submit handler: the fields
 * are only marked invalid once React has re-rendered, so the focus has to
 * wait for that commit rather than happen in the handler itself. A second
 * refused submit with the same errors still moves focus (the tick changes).
 * `fallback` gets focus when no field is invalid (e.g. only a date is
 * missing).
 */
export function useFocusFirstInvalid(
  root: { current: ParentNode | null },
  fallback?: { current: HTMLElement | null },
): () => void {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!tick) return;
    if (!focusFirstInvalid(root.current) && fallback?.current) {
      fallback.current.focus();
    }
  // Only a new request moves focus; refs are read at that moment.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick]);
  return useCallback(() => setTick((n) => n + 1), []);
}
